/**
 * La cave › Vins dégustés (/cave/degustes) — carnet chronologique des vins goûtés ailleurs.
 * Filtres par contexte, recherche plein texte, tri, saisie complète via « Noter un vin »
 * (photo + OCR + vocal), et fiche éditable en direct au clic.
 */
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Mic, Search } from 'lucide-react';
import { caveApi, fileUrl } from '../../services/api';
import { Spinner, EmptyState, Button, SegmentedControl, PageTitle } from '../../components/ui/kit';
import { WineColorDot, Hearts } from '../../components/wine/wine';
import { TASTING_CONTEXT_FILTERS, TASTING_CONTEXTS } from '../../constants';
import { formatDate } from '../../utils/format';

const API = process.env.REACT_APP_BACKEND_URL;
const SORTS = [{ value: 'date', label: 'Date' }, { value: 'note', label: 'Note' }, { value: 'nom', label: 'Nom' }];
const ctxLabel = (v) => TASTING_CONTEXTS.find((c) => c.value === v)?.label || v;

export default function Degustes() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [context, setContext] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('date');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['degustes', context, q, sort],
    queryFn: () => caveApi.degustes({ context, q, sort }),
  });

  // Ouverture directe d'une dégustation via ?open=<id> (lien depuis « À découvrir ») → fiche bouteille.
  useEffect(() => {
    const openId = params.get('open');
    if (openId) { setParams({}, { replace: true }); navigate(`/cave/cuvee/${openId}`); }
  }, [params, setParams, navigate]);

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="flex items-start justify-between gap-3 mb-4">
        <PageTitle kicker="Carnet" title="Vins dégustés" />
        <Button variant="primary" onClick={() => navigate('/scanner', { state: { mode: 'deguste' } })} data-testid="noter-toggle"><Plus className="w-4 h-4" /> Noter un vin</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <SegmentedControl options={TASTING_CONTEXT_FILTERS} value={context} onChange={setContext} testId="deguste-filter" />
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-n500" />
          <input className="vd-input h-9 pl-8" placeholder="Rechercher (nom, domaine, note, transcription…)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="deguste-search" />
        </div>
        <SegmentedControl options={SORTS} value={sort} onChange={setSort} testId="deguste-sort" />
      </div>

      {isLoading ? <Spinner /> : rows.length === 0 ? (
        <EmptyState title="Aucun vin dégusté" description="Notez les vins goûtés en salon, au restaurant ou chez des amis." testId="degustes-empty" />
      ) : (
        <ul className="space-y-2" data-testid="degustes-list">
          {rows.map((c) => (
            <li key={c.id}>
              <button onClick={() => navigate(`/cave/cuvee/${c.id}`)} className="w-full text-left vd-card p-3 flex gap-3 hover:bg-surface transition-colors" data-testid={`deguste-${c.id}`}>
                <div className="w-14 h-20 shrink-0 vd-plate rounded overflow-hidden flex items-center justify-center">
                  {c.label_photo ? <img src={fileUrl(c.label_photo)} alt="" className="w-full h-full object-cover" />
                    : <WineColorDot color={c.color} size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <WineColorDot color={c.color} />
                    <span className="font-title text-xl">{c.name}</span>
                    {c.hearts > 0 && <Hearts count={c.hearts} size={13} />}
                    {c.voice_count > 0 && <Mic className="w-3.5 h-3.5 text-gold700" title="Notes vocales" data-testid={`deguste-mic-${c.id}`} />}
                  </div>
                  <p className="text-[12px] text-n500">{c.domain_name}</p>
                  {c.presentation && <p className="text-[13px] text-n700 italic line-clamp-2 mt-0.5">{c.presentation}</p>}
                  <p className="text-[12px] text-n500 mt-1">
                    {ctxLabel(c.tasting_context)}{c.place ? ` · ${c.place}` : ''}{c.tasting_date ? ` · ${formatDate(c.tasting_date)}` : ''}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
